import { createHttpClient } from './shared/httpClient';

const getGraphqlEndpoint = () =>
  process.env.NODE_ENV === 'development'
    ? '/api/graphql'
    : `${process.env.REACT_APP_WORKPLACE_GRAPHQL_URL}/graphql`;

const workplaceClient = createHttpClient({ withAuth: true });

// Organization service for fetching organization data
export interface Organization {
  id: string;
  name: string;
  logoUrl?: string;
  domain?: string;
}

export interface Membership {
  organization: Organization;
  role: string;
  status: string;
}

export interface ProfileResponse {
  profile: {
    memberships: Membership[];
  };
}

export interface GraphQLError {
  message: string;
  extensions?: {
    code?: string;
  };
}

export interface GraphQLResponse<T> {
  data: T | null;
  errors?: GraphQLError[];
}

export class UserNotActivatedError extends Error {
  code: string;

  constructor(message: string) {
    super(message);
    this.name = 'UserNotActivatedError';
    this.code = 'UNACTIVATED';
  }
}

// GraphQL query for fetching organizations
const FETCH_ORGANIZATIONS_QUERY = `
  query Profile {
    profile {
      memberships {
        organization {
          id
          name
          logoUrl
          domain
        }
        role
        status
      }
    }
  }
`;

/**
 * Fetch organizations from the GraphQL server
 * 
 * @returns Promise<Organization[]> - Array of organizations
 */
export const fetchOrganizations = async (): Promise<Organization[]> => {
  try {
    const data = await workplaceClient.post<GraphQLResponse<ProfileResponse>>(getGraphqlEndpoint(), {
      body: { query: FETCH_ORGANIZATIONS_QUERY },
      mode: 'cors',
      credentials: 'include',
      headers: { Origin: window.location.origin },
    });

    if (data.errors && data.errors.length > 0) {
      // Check for UNACTIVATED error code
      const unactivatedError = data.errors.find(
        error => error.extensions?.code === 'UNACTIVATED'
      );

      if (unactivatedError) {
        throw new UserNotActivatedError(unactivatedError.message);
      }

      throw new Error(`GraphQL errors: ${JSON.stringify(data.errors)}`);
    }

    // Check if data is null
    if (!data.data) {
      throw new Error('No data returned from GraphQL query');
    }

    // Extract organizations from the profile memberships
    const organizations: Organization[] = data.data.profile.memberships.map((membership: Membership) => membership.organization);

    return organizations;
  } catch (error) {
    console.error('Error fetching organizations:', error);
    throw error;
  }
};

/**
 * Get organization by ID
 * 
 * @param id - Organization ID
 * @returns Promise<Organization | null> - Organization or null if not found
 */
export const getOrganizationById = async (id: string): Promise<Organization | null> => {
  const organizations = await fetchOrganizations();
  return organizations.find(org => org.id === id) || null;
};

/**
 * Get user memberships with organization details
 * 
 * @returns Promise<Membership[]> - Array of memberships
 */
export const fetchMemberships = async (): Promise<Membership[]> => {
  try {
    const data = await workplaceClient.post<GraphQLResponse<ProfileResponse>>(getGraphqlEndpoint(), {
      body: { query: FETCH_ORGANIZATIONS_QUERY },
      mode: 'cors',
      credentials: 'include',
      headers: { Origin: window.location.origin },
    });

    if (data.errors && data.errors.length > 0) {
      // Check for UNACTIVATED error code
      const unactivatedError = data.errors.find(
        error => error.extensions?.code === 'UNACTIVATED'
      );

      if (unactivatedError) {
        throw new UserNotActivatedError(unactivatedError.message);
      }

      throw new Error(`GraphQL errors: ${JSON.stringify(data.errors)}`);
    }

    // Check if data is null
    if (!data.data) {
      throw new Error('No data returned from GraphQL query');
    }

    return data.data.profile.memberships;
  } catch (error) {
    console.error('Error fetching memberships:', error);
    throw error;
  }
};
